import SuperFetch from 'library/helpers/superFetch';
import isEqual from 'lodash/isEqual';
import pick from 'lodash/pick';
import { all, call, delay, put, select, takeLatest } from 'redux-saga/effects';
import { Notification } from 'zComponents/atoms';
import { watchActivity } from '../_shared/activity';
import { selectFloorsByFloorID } from './selectors';
import { floorsActions } from './slice';

export function* getFloors() {
	try {
		// Get the orgID from the selected profile
		const { orgID } = yield select((state) => state.Auth.selectedProfile);

		// Get floors from the current organisation.
		const floors = yield call(() => SuperFetch.get('/floors'));

		if (floors.statusCode === 200) {
			yield put(floorsActions.loadFloorsSuccess({ orgID, data: floors.data }));
		} else {
			throw Error(floors.error.message || floors.error || 'Failed to load floors.');
		}
	} catch (error) {
		yield put(floorsActions.loadFloorsError(error));
	}
}

export function* getFloorMaps({ payload }) {
	try {
		// Get the orgID from the selected profile
		const { orgID } = yield select((state) => state.Auth.selectedProfile);

		// Get the floorID from the payload
		const { floorID } = payload;

		// Get the map of the selected floor.
		const map = yield call(() => SuperFetch.get(`/floors/${floorID}/map`));

		if (map.statusCode === 200) {
			yield put(floorsActions.loadFloorMapSuccess({ orgID, floorID, data: map.data }));
		} else {
			throw Error(map.error.message || map.error || 'Failed to load floor plan.');
		}
	} catch (error) {
		yield put(floorsActions.loadFloorMapError(error));
	}
}

export function* createFloor({ payload }) {
	try {
		const { data } = payload;

		// ONE atomic request: the floor and its floor map are created together server-side in a single
		// transaction (POST /floors accepts a nested `map`). This replaces the old create-floor →
		// create-map orchestration, which left an orphaned floor when the map failed.
		// entityID/entityTypeID are set by the backend.
		const floorData = { siteID: data.siteID, name: data.name };
		if (data.note !== undefined) floorData.note = data.note;

		/*
		 * The map is sent ONLY when there is a real image. A space with none leaves `MapID` NULL —
		 * the same "no map" state a media delete produces, so the UI already handles it — and the
		 * first real upload creates the IndoorMap row and links it (routesV5/media/query.js).
		 *
		 * This used to generate a blank white 100x100 PNG in the browser and upload it to Firebase,
		 * purely to satisfy `IndoorMap.Url` being NOT NULL: one junk object in storage per space ever
		 * created, and the last upload the dashboard did outside the broker.
		 */
		if (data.url) floorData.map = { note: data.note, url: data.url };

		const createFloorResponse = yield call(() => SuperFetch.post(`/floors`, floorData));
		if (createFloorResponse.statusCode === 200 && createFloorResponse.data?.floorID) {
			Notification('success', 'Floor created successfully.');
			// Carry the new floorID so the two-step Add Space modal can advance to the photo step.
			yield put(floorsActions.createFloorSuccess({ floorID: createFloorResponse.data.floorID }));
		} else {
			throw Error(createFloorResponse.error?.message || createFloorResponse.error || 'Failed to create floor.');
		}
	} catch (error) {
		yield put(floorsActions.createFloorError(error.message));
		Notification('error', error.message || 'Failed to create floor.');
	}
}

export function* createFloorSuccess() {
	//? The delay was implemented because the floor is created very quickly in the backend.
	//? Without this delay, when we immediately reload the floors, the newly created floor
	//? might not be available yet due to timing issues. This ensures the UI has enough time
	//? to reflect the newly added floor properly.
	yield delay(500);
	yield put(floorsActions.loadFloors());
}

export function* updateFloor({ payload }) {
	try {
		const { floorID, data } = payload;

		const previousData = yield select(selectFloorsByFloorID, floorID);

		// ONE atomic PATCH: floor fields + floor-map upsert (PATCH /floors/:id accepts nested `map`).
		// The backend UPSERTS the map (updates the existing row or inserts one) and resolves its id
		// server-side, so the client no longer sends mapID or entityID/entityTypeID. We still diff so
		// an unchanged map isn't rewritten.
		const floorData = { name: data.name };
		if (data.note !== undefined) floorData.note = data.note;

		const mapChanged = !isEqual(pick(data, ['note', 'url']), pick(previousData, ['note', 'url']));
		if (mapChanged && data.url) {
			// name + dimensions omitted — the backend mirrors the floor name onto the floor plan and
			// owns the map dimensions; the client only sends the image url (and optional note).
			floorData.map = {
				note: data.note,
				url: data.url
			};
		}

		// `call([SuperFetch, 'patch'], url, body)`, NOT `call(() => SuperFetch.patch(...))`. An arrow
		// closes over its arguments, so the yielded effect carries no args and a test cannot see the
		// body — which left "an unchanged map is not rewritten" (the diff above) with no coverage at
		// all. This form puts the url and body in the effect where they can be asserted, and binds
		// `this` to SuperFetch exactly as the method call did.
		const updated = yield call([SuperFetch, 'patch'], `/floors/${floorID}`, floorData);
		if (updated.statusCode !== 200) {
			throw Error(updated.error?.message || updated.error || 'Failed to update floor.');
		}

		yield put(floorsActions.updateFloorSuccess());
		Notification('success', 'Floor updated successfully.');
	} catch (error) {
		yield put(floorsActions.updateFloorError(error));
		Notification('error', error.message || 'Failed to update floor.');
	}
}

export function* updateFloorSuccess() {
	// Reload the floors after Updating a floor
	yield put(floorsActions.loadFloors());
}

// ✅ Handle single or multiple space deletions via the bulk endpoint.
// The backend deletes each floor independently — if it reports a per-floor
// { deleted, failed } summary, partial failures are surfaced without aborting.
export function* deleteFloor({ payload }) {
	try {
		const { floorIDs } = payload;

		// Bulk endpoint accepts at most 50 IDs per request
		const chunks = [];
		for (let i = 0; i < floorIDs.length; i += 50) chunks.push(floorIDs.slice(i, i + 50));

		const responses = yield all(
			chunks.map((chunk) => call(() => SuperFetch.delete('/floors/bulk', { floorIDs: chunk })))
		);

		let deleted = 0;
		let failed = 0;
		responses.forEach((res, i) => {
			if (![200, 204].includes(res.statusCode)) {
				throw Error(res.error?.message || res.error || 'Failed to delete space(s).');
			}
			const summary = res.data?.summary || res.summary;
			deleted += summary ? summary.deleted || 0 : chunks[i].length;
			failed += summary?.failed || 0;
		});

		if (deleted === 0 && failed > 0) {
			throw Error(`Failed to delete ${failed} space${failed > 1 ? 's' : ''}.`);
		}

		yield put(floorsActions.deleteFloorSuccess());

		if (failed > 0) {
			Notification('warning', 'Some spaces were not deleted', `${deleted} deleted, ${failed} failed.`);
		} else {
			Notification(
				'success',
				deleted > 1 ? 'Spaces deleted' : 'Space deleted',
				deleted > 1 ? `${deleted} spaces have been permanently deleted.` : 'Space has been permanently deleted.'
			);
		}
	} catch (error) {
		yield put(floorsActions.deleteFloorError(error));
		Notification('error', 'Delete failed', error.message || 'Failed to delete space.');
	}
}

export function* deleteFloorSuccess() {
	// Reload the floors after Deleting a floor
	yield put(floorsActions.loadFloors());
}

// The main export for the saga
export default function* floorsSaga() {
	yield takeLatest(floorsActions.loadFloors, getFloors);
	yield takeLatest(floorsActions.loadFloorMap, getFloorMaps);
	yield takeLatest(floorsActions.createFloor, createFloor);
	yield takeLatest(floorsActions.createFloorSuccess, createFloorSuccess);
	yield takeLatest(floorsActions.updateFloor, updateFloor);
	yield takeLatest(floorsActions.updateFloorSuccess, updateFloorSuccess);
	yield takeLatest(floorsActions.deleteFloor, deleteFloor);
	yield takeLatest(floorsActions.deleteFloorSuccess, deleteFloorSuccess);
	yield* watchActivity(floorsActions);
}
